import { Ref, Suspense } from "react";
import styled, { css } from "styled-components";
import { Pause, Play, SkipForward } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ArtRoot } from "features/onboarding/art/kit";
import WalkthroughArt from "features/onboarding/art/WalkthroughArt";
import { TradingIntent } from "features/onboarding/guided/intent";
import BotVisual, { hasBotVisual } from "features/onboarding/visuals/BotVisual";
import { getNewLookTokens } from "common/NewLook/tokens";
import { PHONE } from "features/onboarding/guided/walkthrough/WalkthroughLayout";
import {
  ONBOARDING_DEMO_ACTION_ID,
  ONBOARDING_DEMO_CHART_ID,
  ONBOARDING_DEMO_PLAYBACK_ID,
  ONBOARDING_DEMO_RESULT_ID,
} from "testIds";

const MOTION = import.meta.env.VITE_E2E_DISABLE_ANT_MOTION !== "true";

interface WalkthroughPanelProps {
  intent: TradingIntent;
  stage: number;
  total: number;
  /** What the drawing shows at this event (its accessible name). */
  label: string;
  /** What just happened, announced as the example advances. */
  caption: string;
  playing: boolean;
  /** The illustration should be playing (not paused, on screen, not held). */
  running?: boolean;
  /** Bumped when a step is chosen from the list (jump there and play on). */
  seek?: number;
  onStage?: (stage: number) => void;
  onProgress?: (progress: number) => void;
  togglePlayback: () => void;
  advance: () => void;
  /** Name of the "next event" control (it replays after the last one). */
  actionLabel: string;
  chartRef?: Ref<HTMLDivElement>;
}

/**
 * New look: the example's own window (the drawing) on the canvas, with
 * pause/play (and "next event" while paused) as small, quiet icon buttons under its
 * bottom-right corner (playback holds while the pointer or keyboard focus is on
 * the illustration). No frame around it: the drawn window is the panel. The
 * event's words live in the drawing's toast and in a visually hidden status
 * line; the steps list next to the panel tells the story.
 */
const WalkthroughPanel = ({
  intent,
  stage,
  total,
  label,
  caption,
  playing,
  running,
  seek,
  onStage,
  onProgress,
  togglePlayback,
  advance,
  actionLabel,
  chartRef,
}: WalkthroughPanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const note = t("intent.simulation_note");
  const playbackLabel = t(
    playing ? "revision.pause_example" : "revision.play_example"
  );
  return (
    <Panel data-testid={ONBOARDING_DEMO_RESULT_ID} aria-label={note}>
      <Chart
        ref={chartRef}
        data-testid={ONBOARDING_DEMO_CHART_ID}
        data-stage={stage}
      >
        {hasBotVisual(intent) && onStage && onProgress ? (
          // The website's bot illustration, following the example's event.
          <Window role="img" aria-label={label}>
            <BotVisual
              method={intent}
              runtime={{
                stage,
                playing: !!running,
                seek,
                onStage,
                onProgress,
              }}
            />
          </Window>
        ) : (
          <ArtRoot>
            <Suspense fallback={<ArtFallback />}>
              <WalkthroughArt
                intent={intent}
                stage={stage}
                total={total}
                label={label}
              />
            </Suspense>
          </ArtRoot>
        )}
        <Status role="status">{caption}</Status>
        <Controls $flush={hasBotVisual(intent) && !!onStage}>
          {!playing && (
            <IconButton
              type="button"
              onClick={advance}
              aria-label={actionLabel}
              title={actionLabel}
              data-testid={ONBOARDING_DEMO_ACTION_ID}
            >
              <SkipForward size={13} strokeWidth={1.9} aria-hidden />
            </IconButton>
          )}
          <IconButton
            type="button"
            onClick={togglePlayback}
            aria-label={playbackLabel}
            title={playbackLabel}
            data-testid={ONBOARDING_DEMO_PLAYBACK_ID}
          >
            {playing ? (
              <Pause size={13} strokeWidth={1.9} aria-hidden />
            ) : (
              <Play size={13} strokeWidth={1.9} aria-hidden />
            )}
          </IconButton>
        </Controls>
      </Chart>
    </Panel>
  );
};

export default WalkthroughPanel;

const Panel = styled.section`
  position: relative;
  min-width: 0;
  padding: 0;
  ${MOTION &&
  css`
    @media (prefers-reduced-motion: no-preference) {
      /* The event's toast: in, a moment, out. */
      .ak-brief {
        animation: walkthrough-brief 2.6s cubic-bezier(0.16, 1, 0.3, 1)
          var(--ak-delay, 0s) both;
      }
      .ak-fade {
        animation: walkthrough-fade 0.35s ease var(--ak-delay, 0s) both;
      }
    }
    @keyframes walkthrough-fade {
      from {
        opacity: 0;
      }
    }
    @keyframes walkthrough-brief {
      0% {
        opacity: 0;
        transform: translateY(6px);
      }
      12% {
        opacity: 1;
        transform: none;
      }
      78% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  `}
`;
const Window = styled.div`
  width: 100%;
`;
const Controls = styled.div<{ $flush: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin: 8px 0 0;
  /* The old illustrations: line up with the drawn window (inset 10 of 560 units). */
  padding: ${(p) => (p.$flush ? "0" : "0 1.8%")};
  /* Phones: in the card's corner, on the note's line, not a row of their own. */
  ${(p) =>
    p.$flush &&
    css`
      @media (max-width: ${PHONE}px) {
        position: absolute;
        right: 0;
        bottom: 10px;
        margin: 0;
      }
    `}
`;
const IconButton = styled.button`
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  border-radius: 50%;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  opacity: 0.85;
  cursor: pointer;
  transition: color 150ms ease, border-color 150ms ease;
  &:hover,
  &:focus-visible {
    opacity: 1;
  }
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
    border-color: ${(p) => p.theme.colors.primaryBlue};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Chart = styled.div`
  position: relative;
`;
const ArtFallback = styled.div`
  aspect-ratio: 16 / 10;
`;
const Status = styled.p`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
